import { HSL_HUE_RULES, HSL_LIGHTNESS_RULES, HSL_SATURATION_RULES } from '../../constants/rules';
import { HSL_ZH_TEMPLATES } from '../../constants/templates/hsl/zh';
import { HSL_EN_TEMPLATES } from '../../constants/templates/hsl/en';

export class HslConclusionGenerator {
    constructor(language = 'zh') {
        this.setLanguage(language);
    }

    setLanguage(language) {
        this.language = language === 'en' ? 'en' : 'zh';
        this.templates = this.language === 'en' ? HSL_EN_TEMPLATES : HSL_ZH_TEMPLATES;
    }

    buildSection(title, rules) {
        if (!rules || rules.length === 0) return '';
        const sentenceSeparator = this.templates.common.sentenceSeparator;
        const content = rules.join(sentenceSeparator);
        return `${title}${this.templates.common.colon}${content}${this.templates.common.period}`;
    }

    generateLightnessConclusion(globalData, partitionsData) {
        if (!globalData?.lightness) return '';
        const rules = [
            ...HSL_LIGHTNESS_RULES.BASIC_LIGHTNESS.condition(globalData, partitionsData),
            ...HSL_LIGHTNESS_RULES.FEATURE_RULES.condition(globalData, partitionsData),
            ...HSL_LIGHTNESS_RULES.DETAIL_FEATURES.condition(partitionsData),
            ...HSL_LIGHTNESS_RULES.PARTITION_FEATURES.condition(partitionsData)
        ].map((rule) => this.templates.rules.lightness[rule]).filter(Boolean);

        return this.buildSection(this.templates.labels.lightness, rules);
    }

    generateHueConclusion(globalData, partitionsData) {
        if (!globalData?.hue) return '';
        const rules = [];
        if (HSL_HUE_RULES.OVERALL_HUE_CONSISTENCY.condition(globalData.hue, partitionsData)) {
            rules.push(this.templates.rules.hue.OVERALL_HUE_CONSISTENCY);
        }

        const variations = HSL_HUE_RULES.OVERALL_HUE_VARIATIONS.condition(globalData.hue, partitionsData);
        if (variations) {
            const partitionNames = variations
                .map(partition => this.templates.mappings.partitions[partition.name] || partition.name)
                .join(this.language === 'en' ? ', ' : '、');
            const variationRule = this.templates.rules.hue.OVERALL_HUE_VARIATIONS;
            const suffix = partitionNames
                ? `${this.templates.brackets.left}${partitionNames}${this.templates.brackets.right}`
                : '';
            rules.push(`${variationRule}${suffix}`);
        }

        if (HSL_HUE_RULES.SHADOW_HIGHLIGHT_HUE_CONTRAST.condition(globalData.hue, partitionsData)) {
            rules.push(this.templates.rules.hue.SHADOW_HIGHLIGHT_HUE_CONTRAST);
        }

        return this.buildSection(this.templates.labels.hue, rules);
    }

    generateSaturationConclusion(globalData, partitionsData) {
        if (!globalData?.saturation) return '';
        const rules = [];
        if (HSL_SATURATION_RULES.OVERALL_SATURATION_CONSISTENCY.condition(globalData.saturation, partitionsData)) {
            rules.push(this.templates.rules.saturation.OVERALL_SATURATION_CONSISTENCY);
        }

        const variations = HSL_SATURATION_RULES.OVERALL_SATURATION_VARIATIONS.condition(globalData.saturation, partitionsData);
        if (variations) {
            const partitionNames = variations
                .map(partition => this.templates.mappings.partitions[partition.name] || partition.name)
                .join(this.language === 'en' ? ', ' : '、');
            const variationRule = this.templates.rules.saturation.OVERALL_SATURATION_VARIATIONS;
            const suffix = partitionNames
                ? `${this.templates.brackets.left}${partitionNames}${this.templates.brackets.right}`
                : '';
            rules.push(`${variationRule}${suffix}`);
        }

        if (HSL_SATURATION_RULES.SHADOW_HIGHLIGHT_SATURATION_CONTRAST.condition(globalData.saturation, partitionsData)) {
            rules.push(this.templates.rules.saturation.SHADOW_HIGHLIGHT_SATURATION_CONTRAST);
        }

        return this.buildSection(this.templates.labels.saturation, rules);
    }

    generateOverallConclusion(globalData, partitionsData) {
        const sections = [
            this.generateLightnessConclusion(globalData, partitionsData),
            this.generateHueConclusion(globalData, partitionsData),
            this.generateSaturationConclusion(globalData, partitionsData)
        ].filter(Boolean);

        if (sections.length === 0) return this.templates.noData;

        return sections.join('\n\n');
    }
}
